import type { Provider } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import {
  PUSH_BROWSER_ENV,
  PUSH_NATIVE_PORT,
  PUSH_WEB_PORT,
  type PushBrowserEnv,
  type PushNativePort,
  type PushWebPort,
} from './push.service';
import type { PushPayload } from './push.types';

/**
 * Phase 15 (PUSH-01) — production wiring for PushService's DI ports.
 *
 * The actual SDK imports (`@capacitor/push-notifications`, `firebase/app`,
 * `firebase/messaging`) are LOADED LAZILY inside the port methods so:
 *   1. Spec runs never pull them (specs override the tokens directly);
 *   2. Users that never grant permission don't pay the bundle cost on first
 *      paint — Firebase Web SDK alone is ~50 KB compressed.
 */

function buildNativePort(): PushNativePort {
  const isNative = Capacitor.isNativePlatform();
  return {
    isNative,
    getPlatform: () => Capacitor.getPlatform(),
    async requestPermissions() {
      if (!isNative) return { receive: 'denied' };
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const status = await PushNotifications.requestPermissions();
      // Capacitor's PermissionState includes 'prompt-with-rationale' on Android;
      // collapse it down to our narrower 3-tuple.
      const r = status.receive;
      const receive: 'granted' | 'denied' | 'prompt' =
        r === 'granted' || r === 'denied' ? r : 'prompt';
      return { receive };
    },
    async register() {
      if (!isNative) return;
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.register();
    },
    async addListener(eventName, cb) {
      if (!isNative) return undefined;
      const { PushNotifications } = await import('@capacitor/push-notifications');
      // Cast through unknown: Capacitor's listener overloads are tight enough
      // that the discriminated payload type doesn't match our unknown-callback.
      return (PushNotifications as unknown as {
        addListener: (e: string, cb: (p: unknown) => void) => Promise<unknown>;
      }).addListener(eventName, cb);
    },
    async removeAllListeners() {
      if (!isNative) return;
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.removeAllListeners();
    },
  };
}

function buildWebPort(): PushWebPort {
  let onMessageUnsubscribe: (() => void) | null = null;
  return {
    async requestPermission() {
      if (typeof Notification === 'undefined') return 'denied';
      return Notification.requestPermission();
    },
    async registerServiceWorker(path) {
      if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
        throw new Error('serviceWorker unsupported');
      }
      return navigator.serviceWorker.register(path);
    },
    async getFcmToken({ firebaseWeb, serviceWorkerRegistration }) {
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken } = await import('firebase/messaging');
      const app = getApps().length ? getApps()[0] : initializeApp(firebaseWeb);
      const messaging = getMessaging(app);
      try {
        return await getToken(messaging, {
          vapidKey: firebaseWeb.vapidKey,
          serviceWorkerRegistration,
        });
      } catch (err) {
        console.warn('[push] firebase getToken failed', err);
        return null;
      }
    },
    onMessage(handler) {
      // Lazy: wire onMessage only when caller actually subscribes. Returns an
      // unsubscribe so re-registers don't stack listeners.
      void (async () => {
        try {
          const { getApps } = await import('firebase/app');
          const { getMessaging, onMessage } = await import('firebase/messaging');
          if (!getApps().length) return;
          const messaging = getMessaging();
          onMessageUnsubscribe = onMessage(messaging, (raw: {
            notification?: { title?: string; body?: string };
            data?: Record<string, unknown>;
          }) => {
            const payload: PushPayload = {
              title: raw.notification?.title ?? '',
              body: raw.notification?.body ?? '',
              data: raw.data,
            };
            handler(payload);
          });
        } catch (err) {
          console.warn('[push] onMessage setup failed', err);
        }
      })();
      return () => {
        onMessageUnsubscribe?.();
        onMessageUnsubscribe = null;
      };
    },
  };
}

function buildBrowserEnv(): PushBrowserEnv {
  return {
    hasNotificationApi: typeof Notification !== 'undefined',
    hasServiceWorker:
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    firebaseWeb: environment.firebaseWeb,
  };
}

export const pushPortProviders: Provider[] = [
  { provide: PUSH_NATIVE_PORT, useFactory: buildNativePort },
  { provide: PUSH_WEB_PORT, useFactory: buildWebPort },
  { provide: PUSH_BROWSER_ENV, useFactory: buildBrowserEnv },
];
